<template>
  <div>
    <admin-nav active="clients" />
    <b-container fluid>
      <b-row class="mb-1 align-items-end">
        <b-col>
          <h4 class="mb-0">
            Clients
          </h4>
          <p class="text-muted small mb-0">
            Base historique, toutes marques confondues.
          </p>
        </b-col>
        <b-col
          cols="12"
          md="5"
        >
          <b-input-group>
            <b-form-input
              v-model="recherche"
              placeholder="Nom, prénom ou email"
              @keyup.enter="chercher"
            />
            <b-input-group-append>
              <b-button
                variant="outline-primary"
                @click="chercher"
              >
                Chercher
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>

      <!-- Même règle que l'afficheur du référentiel, et pour une raison plus
           forte : ces tables sont alimentées par le backend de gestion, et la
           consigne du dépôt est de ne pas y toucher. -->
      <b-alert
        variant="secondary"
        show
        class="small"
      >
        <strong>Lecture seule.</strong> Les fiches clients sont alimentées par
        le backend de gestion. Elles se modifient là-bas.
      </b-alert>

      <div
        v-if="chargement"
        class="text-center my-3"
      >
        <b-spinner small />
      </div>

      <template v-else>
        <b-table-simple
          v-if="clients.length"
          small
          hover
          responsive
        >
          <b-thead>
            <b-tr>
              <b-th>Nom</b-th>
              <b-th>Email</b-th>
              <b-th>Téléphone</b-th>
              <b-th>Ville</b-th>
              <b-th>Marques</b-th>
              <b-th />
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="c in clients"
              :key="c.ui"
            >
              <b-td>
                {{ c.civilite }} {{ c.nom }} {{ c.prenom }}
              </b-td>
              <b-td>{{ c.email || '—' }}</b-td>
              <b-td>{{ c.port || c.tel || '—' }}</b-td>
              <b-td>{{ c.ville || '—' }}</b-td>
              <b-td>
                <b-badge
                  v-for="s in c.societes"
                  :key="s.ui"
                  variant="light-primary"
                  class="mr-25"
                >
                  {{ s.libelle }}
                </b-badge>
                <span
                  v-if="!c.societes.length"
                  class="text-muted small"
                >
                  aucune
                </span>
              </b-td>
              <b-td class="text-right">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="ouvrir(c)"
                >
                  Fiche
                </b-button>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <b-alert
          v-else
          variant="warning"
          show
        >
          Aucun client ne correspond à cette recherche.
        </b-alert>

        <b-pagination
          v-if="total > parPage"
          v-model="page"
          :total-rows="total"
          :per-page="parPage"
          align="center"
          @change="changerPage"
        />
        <p class="text-muted small text-center">
          {{ total }} client{{ total > 1 ? 's' : '' }}
        </p>
      </template>

      <b-modal
        id="modal-client"
        size="xl"
        :title="titreFiche"
        ok-only
        ok-title="Fermer"
        scrollable
      >
        <div v-if="fiche">
          <b-row class="mb-1">
            <b-col md="6">
              <h6>Coordonnées</h6>
              <p class="mb-0">
                {{ fiche.client.civilite }} {{ fiche.client.nom }} {{ fiche.client.prenom }}<br>
                <span v-if="fiche.client.adresse1">{{ fiche.client.adresse1 }}<br></span>
                <span v-if="fiche.client.adresse2">{{ fiche.client.adresse2 }}<br></span>
                <span v-if="fiche.client.cp || fiche.client.ville">
                  {{ fiche.client.cp }} {{ fiche.client.ville }}<br>
                </span>
                <span v-if="fiche.client.email">{{ fiche.client.email }}<br></span>
                <span v-if="fiche.client.port || fiche.client.tel">
                  {{ fiche.client.port || fiche.client.tel }}
                </span>
              </p>
            </b-col>
            <b-col md="6">
              <h6>Accès à l'espace client</h6>
              <!-- La question qu'on se pose en ouvrant une fiche : pourquoi ce
                   client n'arrive-t-il pas à se connecter. Le verdict vient de
                   la même règle que le parcours réel. -->
              <b-badge :variant="fiche.acces.autorise ? 'light-success' : 'light-danger'">
                {{ fiche.acces.autorise ? 'autorisé' : 'refusé' }}
              </b-badge>
              <span class="text-muted small ml-50">
                règle « {{ fiche.acces.regle }} »
                <template v-if="!fiche.acces.autorise && fiche.acces.motif">
                  — {{ fiche.acces.motif }}
                </template>
              </span>
              <div class="mt-50">
                <b-badge
                  v-for="s in fiche.societes"
                  :key="s.ui"
                  variant="light-primary"
                  class="mr-25"
                >
                  {{ s.libelle }}
                </b-badge>
              </div>
            </b-col>
          </b-row>

          <!-- APERÇU — superadmin seulement. Le bouton n'est même pas
               affiché aux autres : proposer une action qui finira en 403
               fait perdre du temps et donne l'impression d'une panne. -->
          <div
            v-if="estSuperadmin"
            class="mb-1"
          >
            <b-button
              size="sm"
              variant="outline-warning"
              :disabled="apercuEnCours"
              @click="ouvrirApercu"
            >
              <b-spinner
                v-if="apercuEnCours"
                small
                class="mr-50"
              />
              Voir comme le client
            </b-button>
            <span class="text-muted small ml-50">
              ouvre l'espace client en lecture seule, quelques minutes, et
              c'est journalisé
            </span>
            <b-alert
              v-if="apercuErreur"
              variant="danger"
              show
              class="mt-1 mb-0 small"
            >
              {{ apercuErreur }}
            </b-alert>
          </div>

          <b-tabs small>
            <b-tab :title="`Contrats (${fiche.contrats.length})`">
              <b-table-simple
                v-if="fiche.contrats.length"
                small
                responsive
                class="mt-1 mb-0"
              >
                <b-thead>
                  <b-tr>
                    <b-th>Numéro</b-th>
                    <b-th>Marque</b-th>
                    <b-th>Entrée</b-th>
                    <b-th>Sortie</b-th>
                    <b-th class="text-right">
                      TTC
                    </b-th>
                    <b-th />
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="c in fiche.contrats"
                    :key="c.ui"
                  >
                    <b-td>{{ c.numero }}</b-td>
                    <b-td>{{ c.societe ? c.societe.libelle : '—' }}</b-td>
                    <b-td>{{ c.dateEntree || '—' }}</b-td>
                    <b-td>{{ c.dateSortie || '—' }}</b-td>
                    <b-td class="text-right">
                      {{ euros(c.totalTTC) }}
                    </b-td>
                    <b-td>
                      <b-badge
                        v-if="c.preavis"
                        variant="light-warning"
                      >
                        préavis
                      </b-badge>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mt-1 mb-0"
              >
                Aucun contrat.
              </p>
            </b-tab>

            <b-tab :title="`Factures (${fiche.factures.length})`">
              <b-table-simple
                v-if="fiche.factures.length"
                small
                responsive
                class="mt-1 mb-0"
              >
                <b-thead>
                  <b-tr>
                    <b-th>Numéro</b-th>
                    <b-th>Marque</b-th>
                    <b-th>Date</b-th>
                    <b-th class="text-right">
                      TTC
                    </b-th>
                    <b-th class="text-right">
                      Reste
                    </b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="f in fiche.factures"
                    :key="f.ui"
                  >
                    <b-td>{{ f.numero }}</b-td>
                    <b-td>{{ f.societe ? f.societe.libelle : '—' }}</b-td>
                    <b-td>{{ f.date || '—' }}</b-td>
                    <b-td class="text-right">
                      {{ euros(f.totalTTC) }}
                    </b-td>
                    <b-td class="text-right">
                      <b-badge :variant="f.payee ? 'light-success' : 'light-danger'">
                        {{ f.payee ? 'payée' : euros(f.reste) }}
                      </b-badge>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mt-1 mb-0"
              >
                Aucune facture.
              </p>
            </b-tab>

            <b-tab :title="`Documents (${fiche.documents.length})`">
              <b-table-simple
                v-if="fiche.documents.length"
                small
                responsive
                class="mt-1 mb-0"
              >
                <b-tbody>
                  <b-tr
                    v-for="d in fiche.documents"
                    :key="d.ui"
                  >
                    <b-td>{{ d.date || '—' }}</b-td>
                    <b-td>{{ d.societe ? d.societe.libelle : '—' }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mt-1 mb-0"
              >
                Aucun document.
              </p>
            </b-tab>

            <b-tab :title="`Demandes (${fiche.demandes.length})`">
              <b-table-simple
                v-if="fiche.demandes.length"
                small
                responsive
                class="mt-1 mb-0"
              >
                <b-tbody>
                  <b-tr
                    v-for="d in fiche.demandes"
                    :key="d.ui"
                  >
                    <b-td>{{ d.date || '—' }}</b-td>
                    <b-td>{{ d.societe ? d.societe.libelle : '—' }}</b-td>
                    <b-td>{{ d.type === 1 ? 'Dédite' : 'Changement de box' }}</b-td>
                    <b-td>{{ d.dateFin || '' }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mt-1 mb-0"
              >
                Aucune demande.
              </p>
            </b-tab>
          </b-tabs>
        </div>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BAlert,
  BBadge,
  BButton,
  BSpinner,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
  BModal,
  BTabs,
  BTab,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
} from 'bootstrap-vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    AdminNav,
    BContainer,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BButton,
    BSpinner,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    BModal,
    BTabs,
    BTab,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },
  data() {
    return {
      clients: [],
      recherche: '',
      page: 1,
      parPage: 25,
      total: 0,
      chargement: false,
      fiche: null,
      ficheUI: '',
      apercuEnCours: false,
      apercuErreur: '',
    }
  },
  computed: {
    estSuperadmin() {
      const brut = localStorage.getItem('domAdminData')
      if (!brut) return false
      try {
        return JSON.parse(brut).superadmin === true
      } catch (e) {
        return false
      }
    },

    titreFiche() {
      if (!this.fiche) return 'Fiche client'
      const { client } = this.fiche
      return `${client.nom} ${client.prenom}`.trim() || 'Fiche client'
    },
  },
  created() {
    this.charger()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },

    euros(valeur) {
      const n = Number(valeur)
      if (Number.isNaN(n)) return '—'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(n)
    },

    chercher() {
      this.page = 1
      this.charger()
    },

    changerPage(p) {
      this.page = p
      this.charger()
    },

    charger() {
      this.chargement = true
      const params = { page: this.page, parPage: this.parPage }
      if (this.recherche) params.recherche = this.recherche
      this.$http.get('/clientadmin/clients', { params, ...this.authHeaders() })
        .then(response => {
          const { data } = response
          if (data.res !== 1) return
          this.clients = data.clients || []
          this.total = data.total || 0
        })
        .catch(() => {
          this.clients = []
          this.total = 0
        })
        .finally(() => {
          this.chargement = false
        })
    },

    // L'aperçu s'ouvre dans un ONGLET SÉPARÉ, et c'est voulu : l'espace
    // client et l'administration posent chacun leurs jetons dans le même
    // navigateur sous des clés distinctes, mais naviguer dans le même onglet
    // ferait perdre la fiche en cours et obligerait à tout rechercher.
    ouvrirApercu() {
      this.apercuEnCours = true
      this.apercuErreur = ''
      this.$http.post(`/clientadmin/client/${this.ficheUI}/apercu`, {}, this.authHeaders())
        .then(response => {
          const { data } = response
          if (data.res !== 1 || !data.lien) {
            this.apercuErreur = data.message || 'L’aperçu n’a pas pu être ouvert.'
            return
          }
          window.open(data.lien, '_blank', 'noopener')
        })
        .catch(error => {
          const reponse = error && error.response
          this.apercuErreur = (reponse && reponse.data && reponse.data.message)
            || 'L’aperçu n’a pas pu être ouvert.'
        })
        .finally(() => {
          this.apercuEnCours = false
        })
    },

    ouvrir(client) {
      this.fiche = null
      this.ficheUI = client.ui
      this.apercuErreur = ''
      this.$bvModal.show('modal-client')
      this.$http.get(`/clientadmin/client/${client.ui}`, this.authHeaders())
        .then(response => {
          const { data } = response
          if (data.res === 1) this.fiche = data
        })
        .catch(() => {})
    },
  },
}
</script>
